import React, { useState } from 'react';

// UI
import { makeStyles, createStyles, Theme } from '@material-ui/core';
import {
  DatePicker,
  MuiPickersUtilsProvider,
  DatePickerProps
} from '@material-ui/pickers';
import MomentUtils from '@date-io/moment';
import { Button } from '@cube3/cubicle/src/core/atoms/Button';
import { Typography } from '@cube3/cubicle/src/core/atoms/Typography';
import {
  HBox,
  Item,
  VBox,
  Grow
} from '@cube3/cubicle/src/core/templates/layout/Flex';
import { isMobile } from 'react-device-detect';
import { SizeVariants } from '@cube3/cubicle/src/theme/themes';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      borderRadius: 8,
      border: `1px solid ${theme.customPalette.line.onBase2.contrast1}`,
      width: 200,
      '& > fieldset': {
        border: 'none'
      },
      ...theme.typographyStyles.body2,
      color: theme.customPalette.primary.textWhiteHighEmphasis,
      height: 48
    },
    popoverOpen: {
      border: `1px solid ${theme.customPalette.line.onBase2.contrast2}`
    },
    fullWidth: { width: '100%' },
    textField: {
      width: isMobile ? 'calc(100vw - 210px)' : 210
      // backgroundColor: theme.customPalette.surfaceState.getSurfaceStateColor(
      //   theme.customPalette.primary.main,
      //   'primary',
      //   ['blackResting2', 'blackResting2']
      // ),
      // borderRadius: 8,
      // ...theme.typographyStyles.body2
    },
    popoverPaper: {
      marginLeft: -43,
      marginTop: 5
    }
  })
);

interface DatePickerFieldUIProps extends DatePickerProps {
  rootClassName?: string;
  popoverPaperClassName?: string;
  showClearButton?: boolean;
  warning?: React.ReactNode;
  handleClear?: () => void;
}

function DatePickerFieldUI(props: DatePickerFieldUIProps) {
  const [isOpen, setOpenState] = useState(false);

  const handleOpen = () => {
    setOpenState(true);
  };

  const handleClose = () => {
    setOpenState(false);
  };

  const classes = useStyles({});
  const { root, popoverPaper, popoverOpen, textField } = classes;
  const {
    format = 'D MMM YYYY',
    placeholder = 'Select a date',
    variant = 'inline',
    autoOk = true,
    fullWidth = false,
    inputVariant = 'outlined',
    popoverPaperClassName,
    rootClassName,
    InputProps = {
      classes: {
        root: [
          root,
          fullWidth && classes.fullWidth,
          props.rootClassName,
          isOpen && popoverOpen
        ]
          .filter(Boolean)
          .join(' ')
      }
    },
    showClearButton = true,
    handleClear,
    warning,
    ...pickerProps
  } = props;

  return (
    <MuiPickersUtilsProvider utils={MomentUtils}>
      <VBox gap={4} align="start">
        <HBox gap={8} style={{ width: fullWidth ? '100%' : 'auto' }}>
          <DatePicker
            format={format}
            placeholder={placeholder}
            InputProps={InputProps}
            variant={variant}
            className={fullWidth ? null : textField}
            inputVariant={inputVariant}
            fullWidth={fullWidth}
            onOpen={handleOpen}
            onClose={handleClose}
            PopoverProps={{
              anchorOrigin: {
                vertical: 'bottom',
                horizontal: 'right'
              },
              classes: {
                paper: [popoverPaper, popoverPaperClassName].join(' ')
              }
            }}
            autoOk={autoOk}
            {...pickerProps}
            label={undefined}
          />
          {showClearButton && pickerProps.value && (
            <div
              style={{
                marginLeft: isMobile ? '' : 8,
                marginTop: isMobile ? '-5px' : ''
              }}
            >
              <Button
                iconLeft={isMobile ? 'delete' : undefined}
                label={isMobile ? '' : 'Clear'}
                disabled={pickerProps.disabled}
                onClick={handleClear}
                buttonStyle="ghost"
                background="secondary"
                size={isMobile ? SizeVariants.lg : undefined}
              />
            </div>
          )}
        </HBox>
        {warning ? (
          typeof warning === 'string' ? (
            <Typography colorVariant="03">{warning}</Typography>
          ) : (
            warning
          )
        ) : null}
      </VBox>
    </MuiPickersUtilsProvider>
  );
}

export { DatePickerFieldUI };
